import { LoaderIcon } from 'lucide-react-native';
import { useEffect } from 'react';
import Animated, {
    cancelAnimation,
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withTiming,
} from 'react-native-reanimated';

import { type ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { Icon } from '../icons/Icon';

interface SpinnerIconProps {
    size?: number;
    color?: ColorTokenKey;
    /** Milliseconds for one full rotation. Defaults to 2000. */
    durationMs?: number;
}

/**
 * An inline rotating loader icon. Use as the `icon` for a button (or wherever
 * an icon-sized element is needed) to indicate an in-progress action such as
 * "Saving…" or "Deleting…".
 */
export const SpinnerIcon = ({
    size = 16,
    color = '$gray900',
    durationMs = 2000,
}: SpinnerIconProps) => {
    const rotation = useSharedValue(0);

    const animatedStyles = useAnimatedStyle(
        () => ({
            transform: [{ rotateZ: `${rotation.value}deg` }],
        }),
        [rotation],
    );

    useEffect(() => {
        rotation.value = withRepeat(
            withTiming(360, {
                duration: durationMs,
                easing: Easing.linear,
            }),
            -1,
            false,
        );
        return () => {
            cancelAnimation(rotation);
        };
    }, [durationMs]);

    return (
        <Animated.View
            style={[
                {
                    width: size,
                    height: size,
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                animatedStyles,
            ]}
        >
            <Icon icon={LoaderIcon} size={size} color={color} />
        </Animated.View>
    );
};
